/**
 * "Add the other seat too" toggle for van, pickup and lorry seat covers
 * (snippets/fp-companion-seat.liquid).
 *
 * Each page offers one or more choices (a JSON list in the element). A van
 * driver cover offers double or single passenger; the menu's "Single passenger
 * cover" link (?seat=single-passenger) turns it round to offer the driver seat.
 *
 * When this cover is personalised, the customer chooses what goes on the added
 * cover: the same logo, a different one (uploaded here, headrest or backrest),
 * or none. A thumbnail shows the added seat as it will be stitched.
 *
 * product-form.js calls prepare(formData) before it adds this cover, then
 * commit() once this cover is in the cart. The chosen cover goes in second,
 * reusing the logo files just uploaded, and re-renders the cart drawer so the
 * drawer shows both lines. It gets the same quantity and, when
 * this cover is personalised, the Branded variant plus every personalisation
 * property from the form, with a placement preview drawn on its own seat.
 */

/**
 * @typedef {Object} SeatChoice
 * @property {string} key
 * @property {string} seat
 * @property {string} label
 * @property {string} title
 * @property {string} url
 * @property {string} previewGroup
 * @property {string} [eyebrow]
 * @property {string} [image]
 * @property {string} seatProperty
 * @property {string} note
 * @property {number} unbrandedId
 * @property {string} unbrandedPrice
 * @property {number | null} brandedId
 * @property {string | null} brandedPrice
 */

/**
 * @typedef {Object} CompanionPlan
 * @property {SeatChoice} choice
 * @property {'same' | 'different' | 'none'} mode
 * @property {FormData} body
 * @property {Map<string, File>} logoFiles
 * @property {Promise<File | null>} preview
 */

const SINGLE_PASSENGER_PARAM = 'single-passenger';

class FpCompanionSeat extends HTMLElement {
  #busy = false;

  /** @type {SeatChoice[]} */
  #choices = [];

  /** @type {string} */
  #choiceKey = '';

  /** @type {MutationObserver | null} */
  #observer = null;

  /** What goes on the added cover when this one is personalised. */
  /** @type {'same' | 'different' | 'none'} */
  #logoMode = 'same';

  /** A different logo for the added cover, processed by the placer. */
  /** @type {{ file: File, workingSrc: string, warpedSrc: string | null, hasBackground: boolean } | null} */
  #altLogo = null;

  /** @type {'headrest' | 'backrest'} */
  #altPosition = 'backrest';

  #altLoading = false;

  /** Identifies the latest upload attempt, including replacement artwork. */
  #altUploadVersion = 0;

  /** Bumped when the customer may have changed this cover's logo, to redraw the thumbnail. */
  #placerVersion = 0;

  #thumbKey = '';
  #thumbUrl = '';
  /** @type {File | null} The thumbnail's image, reused as the added line's placement preview. */
  #thumbFile = null;
  #thumbToken = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #thumbTimer;

  connectedCallback() {
    this.#choices = this.#readChoices();
    const buyingSinglePassenger =
      this.hasAttribute('data-van-driver') &&
      // The page address describes the main product, not a related cover
      // opened inside quick-buy. Those covers retain their own driver choice.
      !this.closest('quick-add-dialog') &&
      new URL(window.location.href).searchParams.get('seat') === SINGLE_PASSENGER_PARAM;

    if (buyingSinglePassenger) {
      this.#markThisCoverAs('Single passenger');
      this.#choices = this.#choices.filter((choice) => choice.key === 'driver-same');
      this.querySelector('[data-companion-choice]')?.remove();
    } else {
      // The driver-same choice only applies when this cover is the passenger one.
      this.#choices = this.#choices.filter((choice) => choice.key !== 'driver-same');
    }
    this.#choiceKey = this.#choices[0]?.key || '';

    this.#toggle?.addEventListener('click', this.#onToggle);
    this.#trigger?.addEventListener('click', this.#onTriggerClick);
    this.#trigger?.addEventListener('keydown', this.#onTriggerKey);
    this.#list?.addEventListener('keydown', this.#onListKey);
    this.#list?.addEventListener('click', this.#onListClick);
    this.addEventListener('change', this.#onLogoControl);
    this.querySelector('[data-companion-note]')?.addEventListener('input', this.#onNoteInput);
    this.querySelector('[data-companion-thumb-button]')?.addEventListener('click', this.#openFilePicker);
    this.#watchPlacer();
    this.#render();
  }

  disconnectedCallback() {
    this.#toggle?.removeEventListener('click', this.#onToggle);
    this.#trigger?.removeEventListener('click', this.#onTriggerClick);
    this.#trigger?.removeEventListener('keydown', this.#onTriggerKey);
    this.#list?.removeEventListener('keydown', this.#onListKey);
    this.#list?.removeEventListener('click', this.#onListClick);
    document.removeEventListener('pointerdown', this.#onOutside, true);
    this.removeEventListener('change', this.#onLogoControl);
    this.querySelector('[data-companion-note]')?.removeEventListener('input', this.#onNoteInput);
    this.#placer?.removeEventListener('click', this.#onPlacerClick, true);
    this.#observer?.disconnect();
    clearTimeout(this.#thumbTimer);
    if (this.#thumbUrl) URL.revokeObjectURL(this.#thumbUrl);
  }

  /** @returns {boolean} */
  isSelected() {
    return this.dataset.selected === 'true' && Boolean(this.#choice);
  }

  /**
   * Step 1 of adding the chosen cover, run before this cover goes to the cart:
   * checks it can be added and builds its line. Its placement preview starts
   * drawing now (or reuses the thumbnail) so it's ready by the time this
   * cover's upload finishes. Returns false to stop the add, null when the
   * toggle is off.
   *
   * @param {FormData} formData - The form data for this cover, logo files already attached.
   * @returns {Promise<CompanionPlan | false | null>}
   */
  async prepare(formData) {
    const choice = this.#choice;
    if (!this.isSelected() || !choice) return null;
    // A second click while the first add is in flight would add the pair twice.
    if (this.#busy) return false;

    const mode = this.#modeFor(choice);
    if (mode === 'different' && this.#altLoading) {
      this.#showError('Your logo is still loading. Please try again in a moment.');
      return false;
    }
    if (mode === 'different' && !this.#altLogo) {
      this.#showError(`Upload the logo for the ${choice.seat} seat cover, or choose Same logo or No logo.`);
      return false;
    }
    const variantId = mode === 'none' ? choice.unbrandedId : choice.brandedId;
    if (!variantId) {
      this.#showError(`Sorry, the ${choice.seat} seat cover can't be added right now.`);
      return false;
    }

    this.#busy = true;
    this.#clearError();
    const body = new FormData();
    body.set('id', String(variantId));
    body.set('quantity', String(formData.get('quantity') || 1));
    /** @type {Map<string, File>} */
    const logoFiles = new Map();
    /** @type {Promise<File | null>} */
    let preview = Promise.resolve(null);

    if (mode === 'same') {
      for (const [key, value] of formData.entries()) {
        if (!key.startsWith('properties[')) continue;
        // This cover's own previews; the added cover gets one of its seat.
        if (key === 'properties[Placement preview]' || key === 'properties[Seat 2 preview]') continue;
        // The logo files already go up with this cover. Keep them aside so
        // commit() can point at the uploaded copies instead of sending them again.
        if (value instanceof File && /^properties\[Logo \d+]$/.test(key)) {
          logoFiles.set(key, value);
          continue;
        }
        // "Driver · Headrest · 145 mm" names the seat it was placed on; name
        // this cover's seat instead, so the embroiderer isn't misled.
        if (/^properties\[Logo \d+ placement]$/.test(key) && typeof value === 'string' && value.includes('·')) {
          body.append(key, value.replace(/^[^·]*·/, `${this.#capitalise(choice.seat)} ·`));
        } else {
          body.append(key, value);
        }
      }
      preview = this.#previewFile(choice);
      body.set('properties[Personalisation copied from]', this.dataset.productTitle || '');
    } else if (mode === 'different' && this.#altLogo) {
      // Its own logo, at the size this cover uses.
      const size = String(formData.get('properties[Logo size]') || '');
      const embroidery = formData.get('properties[Embroidery]');
      const position = this.#capitalise(this.#altPosition);
      if (typeof embroidery === 'string' && embroidery) body.set('properties[Embroidery]', embroidery);
      body.set('properties[Logo count]', '1');
      body.set('properties[Embroidery position]', position);
      if (size) body.set('properties[Logo size]', size);
      body.set('properties[Logo 1]', this.#altLogo.file, this.#altLogo.file.name);
      body.set('properties[Logo 1 placement]', [this.#capitalise(choice.seat), position, size].filter(Boolean).join(' · '));
      preview = this.#previewFile(choice);
      body.set('properties[Logo]', `Different logo from the ${this.dataset.productTitle || 'other cover'}`);
      const note = this.#noteText();
      if (note) body.set('properties[Embroidery note]', note);
    }
    // A second line of the same product needs a seat name to tell it apart,
    // and to show which seat it is for.
    if (choice.seatProperty) body.set('properties[Seat]', choice.seatProperty);

    return { choice, mode, body, logoFiles, preview };
  }

  /**
   * Step 2, run once this cover is in the cart: adds the chosen cover. With
   * the same logo it points at the files Shopify just stored for this cover
   * rather than uploading them a second time.
   *
   * @param {CompanionPlan} plan - From prepare().
   * @param {any} addedItem - Shopify's reply for this cover's add (its properties hold the uploaded file URLs).
   * @param {string} [sections] - Section ids to re-render, so the cart drawer shows both lines.
   * @returns {Promise<{ ok: boolean, sections?: Record<string, string> }>}
   */
  async commit(plan, addedItem, sections) {
    const { choice, body } = plan;
    try {
      for (const [key, file] of plan.logoFiles) {
        const name = key.slice('properties['.length, -1);
        const uploaded = addedItem?.properties?.[name];
        if (typeof uploaded === 'string' && /^https?:\/\//.test(uploaded)) body.set(key, uploaded);
        else body.set(key, file, file.name);
      }
      const preview = await plan.preview;
      if (preview instanceof File) body.set('properties[Placement preview]', preview, preview.name);
      if (sections) body.set('sections', sections);

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const result = await response.json();
      if (!response.ok || result?.status) {
        const reason = result?.description || result?.message || '';
        this.#showError(
          `Your first cover is already in your cart. There was a problem adding the ${choice.seat} seat cover. Check your cart before trying again.${reason ? ` ${reason}` : ''}`
        );
        return { ok: false };
      }
      // Do not turn an unreadable or incomplete reply into a successful pair.
      if (!result || typeof result !== 'object' || Array.isArray(result) ||
          !Number.isSafeInteger(Number(result.id)) || Number(result.id) <= 0) {
        throw new Error('Unconfirmed matching-cover response');
      }
      return { ok: true, sections: result.sections };
    } catch (error) {
      console.error('[fp-companion-seat]', error);
      this.#showError(`Your first cover is already in your cart, but we couldn't confirm the ${choice.seat} seat cover was added. Check your cart before trying again.`);
      return { ok: false };
    } finally {
      this.#busy = false;
    }
  }

  /** Ends an add that stopped before commit() (e.g. this cover's add failed). */
  release() {
    this.#busy = false;
  }

  /**
   * The chosen cover's placement preview: the thumbnail's file if it shows the
   * current state, otherwise drawn now.
   * @param {SeatChoice} choice
   * @returns {Promise<File | null>}
   */
  #previewFile(choice) {
    if (this.#thumbFile && this.#thumbKey === this.#thumbKeyFor(choice)) return Promise.resolve(this.#thumbFile);
    const instead =
      this.#logoMode === 'different' && this.#altLogo ? { logo: this.#altLogo, position: this.#altPosition } : undefined;
    return Promise.resolve(this.#placer?.capturePreviewFor?.(choice.previewGroup, instead)).catch(() => null);
  }

  /**
   * What the thumbnail depends on. The placer's preview version changes on any
   * edit to this cover's logo, so a stale thumbnail is never reused.
   * @param {SeatChoice} choice
   */
  #thumbKeyFor(choice) {
    return [
      choice.previewGroup,
      this.#logoMode,
      this.#placer?.previewVersion ?? this.#placerVersion,
      this.#altUploadVersion,
      this.#altPosition,
    ].join('|');
  }

  /** @returns {SeatChoice[]} */
  #readChoices() {
    try {
      const list = JSON.parse(this.querySelector('[data-companion-options]')?.textContent || '[]');
      return Array.isArray(list) ? list.filter((choice) => choice && choice.unbrandedId) : [];
    } catch {
      return [];
    }
  }

  /** @returns {SeatChoice | undefined} */
  get #choice() {
    return this.#choices.find((choice) => choice.key === this.#choiceKey);
  }

  get #toggle() {
    return this.querySelector('[data-companion-toggle]');
  }

  /** @returns {HTMLButtonElement | null} */
  get #trigger() {
    return this.querySelector('[data-companion-trigger]');
  }

  /** @returns {HTMLElement | null} */
  get #list() {
    return this.querySelector('[data-companion-list]');
  }

  /** @returns {HTMLElement[]} */
  get #options() {
    return [...this.querySelectorAll('[data-companion-list] [role="option"]')].filter(
      (option) => option instanceof HTMLElement && this.#choices.some((choice) => choice.key === option.dataset.value)
    );
  }

  /** The personalisation block for the same product form, if any. */
  get #placer() {
    const form = this.closest('product-form-component')?.querySelector('form');
    if (!form || document.getElementById(this.dataset.formId || '') !== form) return null;
    return /** @type {any} */ (document.querySelector(`branding-logo-placer[data-form-id="${this.dataset.formId}"]`));
  }

  /** Whether this cover is personalised and the added one could be too. */
  /** @param {SeatChoice} choice */
  #isPersonalised(choice) {
    return this.#placer?.getAttribute('data-applied') === 'true' && Boolean(choice.brandedId);
  }

  /**
   * What the added cover gets. Without personalisation on this cover there is
   * nothing to copy, so it goes in plain.
   * @param {SeatChoice} choice
   * @returns {'same' | 'different' | 'none'}
   */
  #modeFor(choice) {
    return this.#isPersonalised(choice) ? this.#logoMode : 'none';
  }

  /**
   * A file name short enough for the card, cut in the middle so the start and
   * the extension still show: "6eb98846cc57…fe4f4.png".
   * @param {string} name
   */
  #shortName(name) {
    if (name.length <= 28) return name;
    const dot = name.lastIndexOf('.');
    const ext = dot > 0 && name.length - dot <= 6 ? name.slice(dot) : '';
    const stem = ext ? name.slice(0, dot) : name;
    return `${stem.slice(0, 14)}…${stem.slice(-6)}${ext}`;
  }

  /** @param {string} value */
  #capitalise(value) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  /**
   * Adds a Seat property to this cover's own line, e.g. when it is bought for
   * the single passenger seat from the menu.
   * @param {string} seat
   */
  #markThisCoverAs(seat) {
    if (!this.dataset.formId || this.querySelector('input[name="properties[Seat]"]')) return;
    // Kept inside this element (which variant changes don't re-render) and tied
    // to the form by id, so choosing Personalised can't morph it away.
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[Seat]';
    input.value = seat;
    input.setAttribute('form', this.dataset.formId);
    this.append(input);
    this.dataset.thisSeat = seat.toLowerCase();
  }

  #onToggle = () => {
    this.dataset.selected = this.isSelected() ? 'false' : 'true';
    this.#clearError();
    this.#render();
  };

  /** @param {string} next */
  #choose(next) {
    if (!this.#choices.some((choice) => choice.key === next)) return;
    this.#choiceKey = next;
    // Choosing a seat is a clear "yes", so tick the toggle too.
    this.dataset.selected = 'true';
    this.#clearError();
    this.#render();
    // A brief fade on the label and price, so the change is noticed.
    this.classList.add('is-switching');
    setTimeout(() => this.classList.remove('is-switching'), 150);
  }

  get #isOpen() {
    return this.#trigger?.getAttribute('aria-expanded') === 'true';
  }

  /** @param {'selected' | 'first' | 'last'} [focus] Which option to highlight. */
  #open(focus = 'selected') {
    const trigger = this.#trigger;
    const list = this.#list;
    if (!trigger || !list) return;
    trigger.setAttribute('aria-expanded', 'true');
    list.closest('[data-companion-dropdown]')?.classList.add('is-open');
    const options = this.#options;
    const start =
      focus === 'first'
        ? options[0]
        : focus === 'last'
          ? options[options.length - 1]
          : options.find((option) => option.dataset.value === this.#choiceKey) || options[0];
    this.#highlight(start);
    list.focus({ preventScroll: true });
    document.addEventListener('pointerdown', this.#onOutside, true);
  }

  /** @param {{ returnFocus?: boolean }} [options] */
  #close({ returnFocus = true } = {}) {
    const trigger = this.#trigger;
    if (!trigger || !this.#isOpen) return;
    trigger.setAttribute('aria-expanded', 'false');
    this.#list?.closest('[data-companion-dropdown]')?.classList.remove('is-open');
    this.#list?.removeAttribute('aria-activedescendant');
    document.removeEventListener('pointerdown', this.#onOutside, true);
    if (returnFocus) trigger.focus();
  }

  /** @param {HTMLElement | undefined} option */
  #highlight(option) {
    if (!option) return;
    for (const item of this.#options) item.classList.toggle('is-active', item === option);
    // No scrollIntoView: the list never scrolls, and on a phone it would
    // scroll the whole page under the customer's finger.
    this.#list?.setAttribute('aria-activedescendant', option.id);
  }

  #onTriggerClick = () => {
    if (this.#isOpen) this.#close();
    else this.#open();
  };

  /** @param {KeyboardEvent} event */
  #onTriggerKey = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.#open(event.key === 'ArrowUp' ? 'last' : 'selected');
    }
  };

  /** @param {KeyboardEvent} event */
  #onListKey = (event) => {
    const options = this.#options;
    const active = options.findIndex((option) => option.classList.contains('is-active'));
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#highlight(options[Math.min(options.length - 1, active + 1)]);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#highlight(options[Math.max(0, active - 1)]);
        break;
      case 'Home':
        event.preventDefault();
        this.#highlight(options[0]);
        break;
      case 'End':
        event.preventDefault();
        this.#highlight(options[options.length - 1]);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (options[active]) this.#choose(options[active].dataset.value || '');
        this.#close();
        break;
      case 'Escape':
        event.preventDefault();
        this.#close();
        break;
      case 'Tab':
        this.#close({ returnFocus: false });
        break;
    }
  };

  /** @param {MouseEvent} event */
  #onListClick = (event) => {
    const option = event.target instanceof Element ? event.target.closest('[role="option"]') : null;
    if (!(option instanceof HTMLElement)) return;
    this.#choose(option.dataset.value || '');
    this.#close();
  };

  /** @param {PointerEvent} event */
  #onOutside = (event) => {
    const dropdown = this.querySelector('[data-companion-dropdown]');
    if (dropdown && event.target instanceof Node && dropdown.contains(event.target)) return;
    this.#close({ returnFocus: false });
  };

  /** Keep prices and help text in step with the Personalised control. */
  #watchPlacer() {
    const placer = this.#placer;
    if (!placer) return;
    this.#observer = new MutationObserver(() => {
      this.#placerVersion += 1;
      this.#render();
    });
    this.#observer.observe(placer, { attributes: true, attributeFilter: ['data-applied'] });
    placer.addEventListener('click', this.#onPlacerClick, true);
  }

  /** @param {SeatChoice} choice */
  #priceFor(choice) {
    return this.#modeFor(choice) === 'none' ? choice.unbrandedPrice : choice.brandedPrice;
  }

  /** @param {Event} event */
  #onLogoControl = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.matches('[data-companion-logo-mode]') && target.checked) {
      const mode = /** @type {'same' | 'different' | 'none'} */ (target.value);
      if (mode === 'different' && this.#logoMode !== 'different' && !this.#altLogo) {
        // Start from where the customer put this cover's logo.
        this.#altPosition = this.#placerPosition();
      }
      this.#logoMode = mode;
      this.#clearError();
      this.#render();
      if (mode === 'different' && !this.#altLogo) this.querySelector('[data-companion-upload-label]')?.focus?.();
    } else if (target.matches('[data-companion-position-input]') && target.checked) {
      this.#altPosition = target.value === 'headrest' ? 'headrest' : 'backrest';
      this.#render();
    } else if (target.matches('[data-companion-file]')) {
      const file = target.files?.[0];
      target.value = '';
      if (file) this.#loadAltLogo(file);
    }
  };

  /** The different logo's note, as the modal trims its own (255 characters). */
  #noteText() {
    const field = this.querySelector('[data-companion-note]');
    return field instanceof HTMLTextAreaElement ? field.value.trim().slice(0, 255) : '';
  }

  #onNoteInput = () => {
    const field = this.querySelector('[data-companion-note]');
    const count = this.querySelector('[data-companion-note-count]');
    if (field instanceof HTMLTextAreaElement && count) count.textContent = `${field.value.length} / 255`;
  };

  #openFilePicker = () => {
    /** @type {HTMLInputElement | null} */ (this.querySelector('[data-companion-file]'))?.click();
  };

  /** @param {File} file */
  async #loadAltLogo(file) {
    const placer = this.#placer;
    if (!placer?.prepareLogo) return;
    const version = ++this.#altUploadVersion;
    this.#altLoading = true;
    // A replacement must not silently submit the previous file, including if
    // decoding fails. Cancelling the file picker never reaches this method.
    this.#altLogo = null;
    this.#logoMode = 'different';
    this.#thumbToken += 1;
    this.#clearError();
    this.#render();
    try {
      const logo = await placer.prepareLogo(file);
      if (version !== this.#altUploadVersion) return;
      this.#altLogo = logo;
      // Preserve a later Same logo / No logo choice made while this loaded.
    } catch {
      if (version === this.#altUploadVersion) this.#showError('Please use a PNG, JPG, WEBP or HEIC file.');
    } finally {
      if (version === this.#altUploadVersion) {
        this.#altLoading = false;
        this.#render();
      }
    }
  }

  /** This cover's logo position, from the Personalised modal. */
  #placerPosition() {
    const value = this.#placer?.querySelector('[ref="positionInput"]')?.value || '';
    return value.toLowerCase().includes('head') ? 'headrest' : 'backrest';
  }

  /** The customer may have changed this cover's logo or position in the modal. */
  #onPlacerClick = () => {
    this.#placerVersion += 1;
    this.#scheduleThumbnail();
  };

  #render() {
    const choice = this.#choice;
    if (!choice) {
      this.hidden = true;
      return;
    }
    const selected = this.isSelected();
    const personalised = this.#isPersonalised(choice);

    this.#toggle?.setAttribute('aria-pressed', String(selected));

    const label = this.querySelector('[data-companion-label]');
    if (label) label.textContent = choice.label;

    const eyebrow = this.querySelector('[data-companion-eyebrow]');
    if (eyebrow) eyebrow.textContent = choice.eyebrow || 'Complete the cab';

    const image = /** @type {HTMLImageElement | null} */ (this.querySelector('[data-companion-image]'));
    if (image) {
      if (choice.image && image.getAttribute('src') !== choice.image) image.src = choice.image;
      image.alt = choice.title || '';
      image.hidden = !choice.image;
    }

    const price = this.querySelector('[data-companion-price]');
    if (price) price.textContent = `+${this.#priceFor(choice)}`;

    const triggerText = this.querySelector('[data-companion-trigger-text]');
    if (triggerText) triggerText.textContent = this.#capitalise(choice.seat);
    for (const option of this.#options) {
      const match = this.#choices.find((item) => item.key === option.dataset.value);
      if (!match) continue;
      option.setAttribute('aria-selected', String(match.key === choice.key));
      const optionPrice = option.querySelector('[data-option-price]');
      if (optionPrice) optionPrice.textContent = `+${this.#priceFor(match)}`;
    }

    const help = this.querySelector('[data-companion-help]');
    if (help) {
      const sameProduct = Boolean(choice.seatProperty);
      let text;
      if (!selected) {
        text = sameProduct
          ? `This cover also fits the ${choice.seat} seat. Adds a second one to your cart.`
          : `Adds the matching ${choice.seat} seat cover for this vehicle to your cart.`;
      } else if (personalised && this.#logoMode === 'same') {
        text = `Both covers go in your cart, with the same logo and position on the ${choice.seat} seat cover.`;
      } else if (personalised && this.#logoMode === 'different') {
        text = 'Both covers go in your cart, each with its own logo.';
      } else if (personalised) {
        text = 'Both covers go in your cart. Only this one is embroidered.';
      } else {
        text = 'Both covers go in your cart when you press Add to cart.';
      }
      if (this.dataset.thisSeat) text = `You're buying this cover for the ${this.dataset.thisSeat} seat. ${text}`;

      help.replaceChildren(document.createTextNode(`${text} `));
      if (choice.note) {
        const note = document.createElement('span');
        note.className = 'fp-companion-seat__note';
        note.textContent = choice.note;
        help.append(note);
      }
      if (!sameProduct) {
        const link = document.createElement('a');
        link.className = 'fp-companion-seat__link';
        link.href = choice.url;
        link.textContent = 'View cover';
        help.append(link);
      }
    }

    this.#renderLogo(choice, selected && personalised);
  }

  /**
   * @param {SeatChoice} choice
   * @param {boolean} show
   */
  #renderLogo(choice, show) {
    const section = this.querySelector('[data-companion-logo]');
    if (!(section instanceof HTMLElement)) return;
    section.hidden = !show;
    if (!show) return;

    const mode = this.#logoMode;
    const legend = this.querySelector('[data-companion-logo-legend]');
    if (legend) legend.textContent = `Logo on the ${choice.seat} cover`;
    for (const input of this.querySelectorAll('[data-companion-logo-mode]')) {
      if (input instanceof HTMLInputElement) input.checked = input.value === mode;
    }
    for (const input of this.querySelectorAll('[data-companion-position-input]')) {
      if (input instanceof HTMLInputElement) input.checked = input.value === this.#altPosition;
    }

    const different = this.querySelector('[data-companion-different]');
    if (different instanceof HTMLElement) different.hidden = mode !== 'different';
    const noteWrap = this.querySelector('[data-companion-note-wrap]');
    if (noteWrap instanceof HTMLElement) noteWrap.hidden = mode !== 'different';
    const position = this.querySelector('[data-companion-position]');
    if (position instanceof HTMLElement) position.hidden = !this.#altLogo;
    const upload = this.querySelector('[data-companion-upload-label]');
    if (upload) upload.textContent = this.#altLoading ? 'Loading…' : this.#altLogo ? 'Change logo' : 'Upload logo';

    const text = this.querySelector('[data-companion-logo-text]');
    if (text instanceof HTMLElement) {
      // The full file name stays available on hover and to screen readers.
      if (mode === 'different' && this.#altLogo) text.title = this.#altLogo.file.name;
      else text.removeAttribute('title');
    }
    if (text) {
      text.textContent =
        mode === 'same'
          ? 'Same logo and position as this cover.'
          : mode === 'none'
            ? 'This cover comes plain, with no embroidery.'
            : this.#altLogo
              ? `${this.#shortName(this.#altLogo.file.name)}, on the ${this.#altPosition}.`
              : `Upload the logo for the ${choice.seat} cover. It's stitched at the same size.`;
    }

    // The thumbnail is a file picker in "Different" mode, a plain image in "Same".
    const pick = this.querySelector('[data-companion-thumb-button]');
    const still = this.querySelector('[data-companion-thumb-static]');
    if (pick instanceof HTMLElement) pick.hidden = mode !== 'different';
    if (still instanceof HTMLElement) still.hidden = mode !== 'same' || !this.#thumbUrl;
    const empty = this.querySelector('[data-companion-thumb-empty]');
    const pickImg = this.querySelector('[data-companion-thumb]');
    const hasAltThumb = mode === 'different' && Boolean(this.#altLogo) && Boolean(this.#thumbUrl);
    if (empty instanceof HTMLElement) empty.hidden = hasAltThumb;
    if (pickImg instanceof HTMLElement) pickImg.hidden = !hasAltThumb;
    pick?.setAttribute('aria-label', this.#altLogo ? 'Change the logo for this cover' : 'Choose a logo file');

    this.#scheduleThumbnail();
  }

  /** Redraw the thumbnail once things settle, not on every render. */
  #scheduleThumbnail() {
    clearTimeout(this.#thumbTimer);
    this.#thumbTimer = setTimeout(() => this.#drawThumbnail(), 200);
  }

  async #drawThumbnail() {
    const choice = this.#choice;
    if (!choice || !this.isSelected() || !this.#isPersonalised(choice)) return;
    const mode = this.#logoMode;
    if (mode === 'none' || (mode === 'different' && !this.#altLogo)) return;

    const key = this.#thumbKeyFor(choice);
    if (key === this.#thumbKey && this.#thumbUrl) return;

    const token = ++this.#thumbToken;
    const frames = this.querySelectorAll('.fp-companion-seat__thumb');
    frames.forEach((frame) => frame.classList.add('is-loading'));
    const instead = mode === 'different' && this.#altLogo ? { logo: this.#altLogo, position: this.#altPosition } : undefined;
    const file = await this.#placer?.capturePreviewFor?.(choice.previewGroup, instead).catch(() => null);
    frames.forEach((frame) => frame.classList.remove('is-loading'));
    if (token !== this.#thumbToken || !(file instanceof File)) return;

    if (this.#thumbUrl) URL.revokeObjectURL(this.#thumbUrl);
    this.#thumbUrl = URL.createObjectURL(file);
    this.#thumbKey = key;
    this.#thumbFile = file;
    const alt = `${this.#capitalise(choice.seat)} seat cover with ${mode === 'same' ? 'the same' : 'its own'} logo on the ${
      mode === 'same' ? this.#placerPosition() : this.#altPosition
    }`;
    for (const img of this.querySelectorAll('[data-companion-thumb], [data-companion-thumb-static]')) {
      if (!(img instanceof HTMLImageElement)) continue;
      img.src = this.#thumbUrl;
      img.alt = alt;
    }
    // Show whichever frame this mode uses now that it has a picture.
    const section = this.querySelector('[data-companion-logo]');
    if (section instanceof HTMLElement && !section.hidden) this.#renderLogo(choice, true);
  }

  /** @param {string} message */
  #showError(message) {
    const error = this.querySelector('[data-companion-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = false;
  }

  #clearError() {
    const error = this.querySelector('[data-companion-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = '';
    error.hidden = true;
  }
}

if (!customElements.get('fp-companion-seat')) {
  customElements.define('fp-companion-seat', FpCompanionSeat);
}
